/**
 * Drag-and-drop payload types.
 *
 * ⚠️ ONE CONSTANT, TWO HALVES. The drag source (CheckInPanel) and the drop
 * targets (GanttCalendar) are in different files and were built a session
 * apart. A hardcoded string in each is how they silently stop matching -- the
 * drag still "works", the drop just never fires, and nothing errors. Both
 * import this.
 *
 * The payload is the checkInId as a plain string. Deliberately NOT JSON: the
 * only thing the grid needs is which arrival was picked up, and it re-reads
 * everything else from state that is already loaded.
 */
export const CHECKIN_DND_MIME = 'application/x-symbia-checkin';

/**
 * ---- ⭐ WHERE THE ARRIVAL IS COMING FROM (2026-09-10) ----------------------
 *
 * ⛔ A SECOND MIME, NOT JSON IN THE FIRST ONE. `CHECKIN_DND_MIME` is read as a
 * bare id in several places; widening it into a JSON blob would break every one
 * of them silently — a `JSON.parse` of a plain id throws, and the drop handler
 * would simply stop attaching.
 *
 * ⭐ IT CARRIES THE APPOINTMENT NUMBER THE ARRIVAL IS ALREADY ON, or is absent
 * when the arrival is unmatched. ⚠️ ABSENT IS THE COMMON CASE and means "nothing
 * to warn about" — the drop then behaves exactly as it always has.
 *
 * ⚠️ THE NUMBER, NOT THE ID. It exists to be shown to a person in a sentence
 * ("A00000310 will be left with no arrival"), and the id is not a thing anyone
 * can recognise on a screen.
 */
export const CHECKIN_FROM_APPT_MIME = 'application/x-symbia-checkin-from';

/**
 * ---- PAPERWORK (#150) -------------------------------------------------------
 *
 * Pete, 2026-08-25: *"We will then drag the document to, or attach the document to the
 * driver or an appointment."*
 *
 * ⭐ A SEPARATE MIME FROM THE ARRIVAL ONE, ON PURPOSE. Both drags end on the same
 * appointment chip and mean completely different things — one attaches a truck to a
 * booking, the other files a bill of lading — and a target that could not tell them
 * apart would do the wrong one. `dataTransfer.types.includes(…)` is how each target
 * decides whether the thing being dragged is for it, so the two must not share a name.
 *
 * ⚠️ AND THIS ONE IS JSON, unlike the arrival's bare id. Two kinds of thing can be
 * dragged and they need different handling on drop:
 *
 *   'scan'     — still in SharePoint. It must be INGESTED first, and only then filed.
 *   'document' — already in Dock, sitting in the tray. Filed directly.
 *
 * The name rides along purely so a drop can say what it just filed without a lookup.
 */
export const PAPERWORK_DND_MIME = 'application/x-symbia-paperwork';

/**
 * ⭐ A DOCK DOOR ROW BEING RE-ORDERED ON THE BOARD (2026-09-10).
 *
 * ⛔ ITS OWN MIME, LIKE EVERY OTHER DRAG HERE. The Gantt already carries three
 * kinds — an appointment, an arrival, a piece of paperwork — over the same rows,
 * and every drop handler decides what it is looking at by TYPE rather than by
 * guessing from the payload. ⚠️ A fourth kind sharing a type would be read by a
 * handler written for one of the other three.
 */
export const DOOR_ROW_DND_MIME = 'application/x-symbia-door-row';

/** ⭐ True only for a door-row reorder — never an appointment, arrival or scan. */
export function isDoorRowDrag(dt: DataTransfer | null): boolean {
    return !!dt && dt.types.includes(DOOR_ROW_DND_MIME);
}

/** ⭐ ONE thing being filed. The unit that reaches a drop handler. */
export type PaperworkDragItem =
    | { kind: 'scan'; itemId: string; name: string }
    | { kind: 'document'; documentId: string; name: string }
    /**
     * ---- ⭐⭐ A FILE IN THE SCANNER'S FOLDER ON THIS PC ---------------------
     *
     * > Pete, 2026-09-10: *"cannot drag that .jpg file to the appointment or
     * > driver."* … *"Should all have the same look and feel if its a local
     * > repository or a sharepoint one?"*
     *
     * ⛔ IT HAS NO ID, AND THAT IS THE WHOLE DIFFICULTY. A `scan` is a
     * SharePoint item and a `document` is a Dock row; a local file is neither —
     * Dock has never seen it. So the payload carries the folder's own `ref` and
     * the drop UPLOADS it first, then files it, which is exactly what the
     * "File…" button did one step at a time.
     *
     * ⚠️ THE BYTES CANNOT RIDE IN THE PAYLOAD. `dataTransfer` takes strings, and
     * reading a file is async while `dragstart` is not — hence the handle
     * registry below rather than a fatter item.
     */
    | { kind: 'localScan'; ref: string; name: string };

/**
 * ⭐⭐ THE WIRE FORMAT IS ALWAYS A LIST NOW (2026-08-27).
 *
 * Pete: *"if you could add checkboxes to each of the Scanned Paperwork items to
 * allow us to drag all the checked attachments to a driver card or appointment."*
 *
 * ⛔⛔ A LIST EVEN FOR ONE DOCUMENT, AND THAT IS THE WHOLE POINT OF THE CHANGE.
 * The obvious alternative — keep the single shape and add an optional `items` —
 * would let a drop target that had not been updated read the first document and
 * file it, **silently dropping the other four**. A scheduler drags five bills of
 * lading, one lands, and nothing anywhere says so.
 *
 * ⭐ CHANGING THE TYPE OUTRIGHT MAKES EVERY UNCONVERTED CALLER A **COMPILE
 * ERROR** instead. There are exactly three sites — the tray, the appointment chip
 * and the driver card — and the compiler now insists all three agree.
 *
 * ⚠️ THE NAMES RIDE ALONG for the same reason they always did: a drop must be able
 * to say what it just filed without a lookup, and with five documents that sentence
 * is the difference between "filed 5 documents" and knowing WHICH five.
 */
export type PaperworkDragPayload = { items: PaperworkDragItem[] };

/**
 * ============================================================================
 * ⭐⭐ IS A PAPERWORK DRAG IN FLIGHT RIGHT NOW
 * ============================================================================
 *
 * > Pete, 2026-09-10: *"now I cannot drag the paperwork from the Scanned
 * > Paperwork to the driver or the appointment. Doesn't attach."*
 *
 * ---- ⛔⛔ FIXING THE HOVER IS WHAT BROKE THE DRAG -------------------------
 *
 * Dwelling on a load switches the tray to Attached Paperwork. ⚠️ THE ROW BEING
 * DRAGGED LIVES IN THE TRAY. So: pick a scan up, carry it over the chip you
 * mean to drop it on, and 350ms later the tray swaps mode and **unmounts the
 * element under the cursor** — the browser ends the drag and the drop never
 * arrives.
 *
 * ⛔ IT IS DETERMINISTIC, NOT A RACE. A drag takes longer than the dwell, and
 * dropping on a chip REQUIRES hovering that chip. Every attempt failed.
 *
 * ⚠️ AND IT ONLY APPEARED NOW BECAUSE APPOINTMENT HOVER HAD BEEN INERT. While
 * the frozen callback (see `GanttCalendar`) stopped the tray switching, the
 * drag worked — one bug was hiding the other, which is why "much better" and
 * "now I cannot drag" arrived in the same message.
 *
 * ---- ⭐ A MODULE FLAG, BECAUSE mouseenter HAS NO DataTransfer -------------
 *
 * `isPaperworkDrag(dt)` needs the event's payload; a hover handler has no such
 * event. ⛔ And DataTransfer contents are unreadable during dragover anyway, by
 * design. So the fact "a drag is in flight" has to be recorded when it starts.
 *
 * ⚠️ CLEARED FROM THE WINDOW, NOT FROM EACH ROW. A row that unmounts mid-drag
 * never runs its own `onDragEnd` — which is precisely the situation here — so a
 * per-row handler would leave the flag stuck on and suppress hover for the rest
 * of the session. The window sees `dragend` and `drop` whatever happens to the
 * source element.
 */
let paperworkDragging = false;
/** ⭐ A5 — the same fact for a WMS order being dragged. See `setWmsOrderDrag`. */
let wmsOrderDragging = false;

/** ⭐ True between picking a scan up and the browser ending the drag. */
export function paperworkDragInProgress(): boolean {
    return paperworkDragging;
}

if (typeof window !== 'undefined') {
    /* ⛔ CAPTURE PHASE, and both events. `drop` fires on a successful drop and
       `dragend` on every drag including a cancelled one; listening for only one
       leaves the flag set in the other case. */
    const clear = () => { paperworkDragging = false; wmsOrderDragging = false; };
    window.addEventListener('dragend', clear, true);
    window.addEventListener('drop', clear, true);
}

export function setPaperworkDrag(dt: DataTransfer, items: PaperworkDragItem[]): void {
    /* ⭐ SET HERE, so every drag source is covered by construction — there is no
       second place a paperwork drag can begin. */
    paperworkDragging = true;
    dt.setData(PAPERWORK_DND_MIME, JSON.stringify({ items } satisfies PaperworkDragPayload));
    /**
     * ⚠️ 'copy', NOT 'link'. Filing paperwork does not move it out of anywhere the
     * scheduler can see — the tray row stays until the list refreshes — and a cursor
     * that promises a move when nothing moves teaches the wrong thing about a control
     * whose whole risk is putting a document somewhere nobody chose.
     */
    /**
     * ---- ⛔⛔ 'copyMove', AND THE NARROWER VALUE SILENTLY KILLED A DROP -------
     *
     * > Pete, 2026-09-10: *"Not letting me at all move the files now???? To any
     * > of the 3 icons"*.
     *
     * ⛔ THE BROWSER REFUSES A DROP WHOSE `dropEffect` IS NOT PERMITTED BY
     * `effectAllowed`. The pile icon asks for `'move'` — correctly, because a
     * document dropped there LEAVES the load — and with `'copy'` declared here
     * the drop event never fired at all. No error, no cursor, nothing: the
     * hardest kind of failure to see, because the drag looks fine right up to
     * the moment it does nothing.
     *
     * ⚠️ THE OLD COMMENT WAS RIGHT ABOUT ITS OWN CASE AND WRONG AS A RULE.
     * Filing onto a load IS a copy — the tray row stays — so 'copy' is the
     * honest cursor there. Unfiling is a genuine move. Both are now legal, and
     * each TARGET names the one it means.
     */
    dt.effectAllowed = 'copyMove';
}

export function isPaperworkDrag(dt: DataTransfer): boolean {
    return dt.types.includes(PAPERWORK_DND_MIME);
}

/**
 * ⛔ RETURNS NULL RATHER THAN THROWING ON ANYTHING UNEXPECTED. A drop handler runs
 * inside a browser event where a throw is swallowed by React and the user sees a drag
 * that simply did nothing. Null is checkable; an exception here is invisible.
 */
/**
 * ============================================================================
 * ⛔⛔⛔ EXHAUSTIVE **BY CONSTRUCTION** — ADDING A VARIANT MUST NOT COMPILE
 * ============================================================================
 *
 * > Pete, 2026-09-10: *"That drag carried nothing to move."*
 *
 * ---- WHAT HAPPENED, AND WHY THE COMPILER DID NOT SAVE ME -------------------
 *
 * `localScan` was added to `PaperworkDragItem` so a file in the scanner's folder
 * could be dragged. The union change DID make the consumer a compile error —
 * `Calendar`'s drop branch — which is exactly what the note on that type
 * promises. ⛔ BUT THIS VALIDATOR NARROWS FROM `unknown`, having just parsed
 * JSON, so TypeScript had nothing to check it against. It went on accepting
 * only 'scan' and 'document', rejected every localScan drag, and returned
 * null — which the drop handlers correctly reported as "carried nothing".
 *
 * ⚠️ SO THE TYPE SYSTEM POINTED AT ONE OF THE TWO PLACES THAT NEEDED CHANGING
 * AND WAS SILENT ABOUT THE OTHER. A check more confident than its scan, §4.14,
 * in the sharpest form yet: the guard was the compiler itself.
 *
 * ---- ⭐⭐ THE FIX IS THE `never` ASSIGNMENT, NOT THE NEW CASE ---------------
 *
 * A `switch` whose `default` assigns the value to `never` FAILS TO COMPILE the
 * moment a variant exists without a case. ⛔ Adding 'localScan' alone would
 * have fixed today and left the next person exactly where I was.
 */
function isValidDragItem(it: PaperworkDragItem): boolean {
    /* ⚠️ `it` is cast from parsed JSON, so at RUNTIME it may be null or carry a
       kind nobody has heard of. Both land in `default` and are refused. */
    switch (it?.kind) {
        case 'scan':
            return typeof it.itemId === 'string' && !!it.itemId;
        case 'document':
            return typeof it.documentId === 'string' && !!it.documentId;
        case 'localScan':
            return typeof it.ref === 'string' && !!it.ref;
        default: {
            /**
             * ⛔⛔ DO NOT DELETE, AND DO NOT ADD A CAST. This line is the whole
             * point: if `PaperworkDragItem` gains a variant and no case is added
             * above, `it` is not `never` here and THIS FAILS TO COMPILE.
             *
             * ⚠️ IT WAS WRITTEN `it as never` FIRST, WHICH PROVED NOTHING — the
             * cast satisfies the annotation whatever `it` is, so a new variant
             * compiled cleanly and the guard was decoration. Caught by adding a
             * throwaway variant and watching the build stay green: the same
             * "check more confident than its scan" this file's own history is
             * made of. ⛔ `as never`, `as any` or `@ts-expect-error` here all
             * disable the only thing this default branch is for.
             */
            const unhandled: never = it;
            void unhandled;
            return false;
        }
    }
}

export function readPaperworkDrag(dt: DataTransfer): PaperworkDragItem[] | null {
    const raw = dt.getData(PAPERWORK_DND_MIME);
    if (!raw) return null;
    try {
        const p = JSON.parse(raw) as { items?: unknown };
        if (!Array.isArray(p?.items)) return null;
        /**
         * ⛔ EVERY ITEM IS VALIDATED, AND ONE BAD ONE FAILS THE WHOLE DRAG.
         *
         * ⚠️ Filtering the rubbish out and filing the rest would be the silent
         * partial success this list format exists to prevent — the scheduler would
         * see "filed 4" after dragging 5 and have no way to learn which one, or
         * that anything was wrong at all. Null is checkable; a short list is not.
         */
        const items = p.items as PaperworkDragItem[];
        if (items.length === 0) return null;
        for (const it of items) {
            if (!isValidDragItem(it)) return null;
        }
        return items;
    } catch {
        return null;
    }
}

// ---------------------------------------------------------------------------
// FILES FROM THE OPERATING SYSTEM — the two facts that keep going wrong
// ---------------------------------------------------------------------------

/**
 * A DROP OF REAL FILES, NEVER THE APP'S OWN DRAG.
 *
 * ⚠️ A card dragged WITHIN the app carries the paperwork MIME type; a file from
 * Explorer or Finder carries `Files`. Without the second half of this test,
 * picking up a scan in the tray and letting go over a drop zone would try to
 * UPLOAD it — re-uploading a file we already have, under a new document id.
 *
 * ⛔ LIVES HERE, BESIDE `isPaperworkDrag`, BECAUSE IT IS THE SAME QUESTION FROM
 * THE OTHER SIDE. Four screens want a drop zone (#303), and four private copies
 * of this two-line test is how three of them end up with three different bugs.
 */
export function isFileDrop(dt: DataTransfer): boolean {
    return dt.types.includes('Files') && !isPaperworkDrag(dt);
}

/**
 * ⛔⛔ TAKE THE FILES OUT OF THE EVENT **SYNCHRONOUSLY**. THIS IS #288.
 *
 * Pete, twice: *"trying to attach .pdf's from a local drive is still not working
 * and not showing any type of indication failed or success."*
 *
 * ⚠️ `DataTransfer.files` IS A **LIVE** FileList. The HTML spec requires it to
 * return the empty list once the drag data store enters protected mode — which
 * happens the instant the `drop` event finishes dispatching. Code that called
 * `Array.from(files)` AFTER an `await getToken()` therefore iterated NOTHING,
 * and every symptom followed from that: the length check passed (it ran before
 * the await), zero requests were made, nothing threw, and the list reloaded
 * unchanged. **"Uploaded nothing" and "uploaded everything" looked identical.**
 *
 * ⚠️ THE BROWSE BUTTON BREAKS THE SAME WAY for a different reason: a change
 * handler that does `e.target.value = ''` clears the very same live list, and it
 * runs the moment an async upload function yields at its first await.
 *
 * ⭐ SO EVERY CALLER GOES THROUGH THIS FUNCTION, and the rule stops being
 * something four screens have to remember.
 */
export function snapshotFiles(list: FileList | null | undefined): File[] {
    return list ? Array.from(list) : [];
}

/**
 * ============================================================================
 * ⭐⭐ THE LOCAL FILES A DRAG MIGHT BE CARRYING
 * ============================================================================
 *
 * ⛔ A `localScan` ITEM IS A TICKET, NOT A FILE. The drop needs the actual
 * `FileSystemFileHandle` to read bytes from, and that object cannot be
 * serialised into a `dataTransfer`. So the tray registers what it is showing
 * and the drop looks the ticket up.
 *
 * ⚠️ REPLACED WHOLESALE ON EVERY FOLDER READ, never appended to. The folder is
 * the truth; a map that only ever grew would hand the drop handler a handle to
 * a file the scanner has since moved, and the failure would be a confusing
 * "not found" at upload time rather than the row simply being gone.
 *
 * ⛔ NOT A REACT CONTEXT. The drop is handled in `Calendar`, the registry is
 * filled by `ScanFolderPanel`, and they are siblings — threading a provider
 * between them to carry a lookup table would put plumbing in six components
 * that have no other use for it.
 */
export interface LocalScanHandle {
    name: string;
    getFile: () => Promise<File>;
}

let localScans = new Map<string, LocalScanHandle>();

/** ⭐ Called by the tray whenever it re-reads the folder. */
export function registerLocalScans(entries: Array<{ ref: string } & LocalScanHandle>): void {
    localScans = new Map(entries.map(e => [e.ref, { name: e.name, getFile: e.getFile }]));
}

/** ⛔ Null when the file has gone since the drag began — the caller must say so. */
export function lookupLocalScan(ref: string): LocalScanHandle | null {
    return localScans.get(ref) ?? null;
}

/**
 * ============================================================================
 * ⭐⭐ THE OTHER DIRECTION — PUTTING A FILE INTO THE FOLDER
 * ============================================================================
 *
 * > Pete, 2026-09-10: *"Still not letting me drag from sharepoint to local."*
 *
 * ⛔ SAME SHAPE AS THE REGISTRY ABOVE AND FOR THE SAME REASON. The folder
 * handle lives in `ScanFolderPanel`; the drop is handled in `Calendar`. They
 * are siblings, so the panel publishes what it can do and the page calls it.
 *
 * ⚠️ `null` IS A MEANINGFUL ANSWER, NOT AN ABSENCE OF ONE: no folder is
 * connected, or this browser has no write half. The drop handler turns that
 * into a sentence naming the fix, which is the difference between this icon
 * teaching and this icon looking broken.
 *
 * ⛔ THE PANEL MUST UNREGISTER when it unmounts or loses the folder. A stale
 * writer would report success against a handle whose permission has lapsed.
 */
export interface LocalFolderWriter {
    /** ⭐ What to show in a message — "Saved to Scans." */
    folderName: string;
    /** Returns the name actually written, which may be suffixed. */
    write: (fileName: string, data: Blob) => Promise<string>;
    /**
     * ---- ⛔⛔ THE OTHER HALF OF "MOVE" (2026-09-10) ------------------------
     *
     * > Pete: *"sometimes it moves, sometimes it copies. Can we always make it
     * > move please?"*
     *
     * ⭐ A SCAN THAT DOCK HAS TAKEN IN LEAVES THE FOLDER, exactly as a SharePoint
     * scan already did (`ingestScan` moves it to `Filed/YYYY-MM`). Without this
     * the workstation tray only ever grows.
     *
     * ⛔⛔ CALL IT ONLY AFTER THE UPLOAD HAS RETURNED A DOCUMENT ID. There is no
     * Recycle Bin behind it — see `deleteFile` in `lib/scanFolder.ts`. Filing
     * first and removing second is the difference between a tidy tray and a lost
     * bill of lading.
     *
     * ⚠️ IT THROWS RATHER THAN RETURNING FALSE, and a caller that has already
     * filed must catch it and report the leftover SEPARATELY. "Could not remove
     * the original" and "could not file this" are different sentences and only
     * one of them means somebody has to act.
     */
    remove: (fileName: string) => Promise<void>;
}

let localFolderWriter: LocalFolderWriter | null = null;

export function registerLocalFolderWriter(w: LocalFolderWriter | null): void {
    localFolderWriter = w;
}

export function lookupLocalFolderWriter(): LocalFolderWriter | null {
    return localFolderWriter;
}

/**
 * ============================================================================
 * ⭐⭐ A5 — A WMS ORDER DRAGGED ONTO AN APPOINTMENT (2026-09-28)
 * ============================================================================
 *
 * > Pete, 2026-09-27: *"drag an order to an appointment an attach it"*.
 *
 * ⛔ ITS OWN MIME, LIKE EVERY OTHER DRAG HERE. The appointment chip already takes
 * an arrival and paperwork, and each handler decides by TYPE what it is holding.
 * An order read by the paperwork handler would try to file it; read by the
 * arrival handler it would try to attach a truck.
 *
 * ⭐ THE PAYLOAD IS THE ORDER'S FULL KEY — source, business unit, type, number —
 * because that is the key of `dock.AppointmentWmsOrder` and of OrdersCache. An
 * order number alone is not unique: two clients can use the same one.
 *
 * ⚠️ A ROW WITH ANY PART OF THE KEY MISSING IS NOT DRAGGABLE (`wmsOrderKeyOf`
 * returns null). The server would refuse it with a 400; refusing to start the
 * drag says the same thing earlier, on the row itself.
 */
export const WMS_ORDER_DND_MIME = 'application/x-symbia-wms-order';

export interface WmsOrderDragKey {
    wmsSource: string;
    businessUnit: string;
    orderType: string;
    orderNumber: string;
}

/** The drag key for a panel row, or null when any part is blank. */
export function wmsOrderKeyOf(row: {
    wmsSource: string | null; businessUnit: string | null; orderType: string | null; orderNumber: string | null;
}): WmsOrderDragKey | null {
    const parts = [row.wmsSource, row.businessUnit, row.orderType, row.orderNumber].map(v => (v ?? '').trim());
    if (parts.some(v => v === '')) return null;
    const [wmsSource, businessUnit, orderType, orderNumber] = parts;
    return { wmsSource, businessUnit, orderType, orderNumber };
}

/** ⭐ True only for an order drag — never an arrival, a document or a door row. */
export function isWmsOrderDrag(dt: DataTransfer | null): boolean {
    return !!dt && dt.types.includes(WMS_ORDER_DND_MIME);
}

/**
 * ⭐ True between picking an order up and the browser ending the drag.
 *
 * ⛔ SAME REASON AS `paperworkDragInProgress`: a hover over an appointment
 * re-scopes the rails, and the rail that re-renders is the one holding the row
 * being dragged. Unmount it mid-drag and the drop never arrives.
 */
export function wmsOrderDragInProgress(): boolean {
    return wmsOrderDragging;
}

export function setWmsOrderDrag(dt: DataTransfer, key: WmsOrderDragKey): void {
    wmsOrderDragging = true;
    dt.setData(WMS_ORDER_DND_MIME, JSON.stringify(key));
    /* ⭐ 'link': the order is not moved or copied anywhere — a row pointing at it
       is added. Each target sets the matching `dropEffect`. */
    dt.effectAllowed = 'link';
}

/** The key carried by an order drag, or null when it is missing or malformed. */
export function readWmsOrderDrag(dt: DataTransfer): WmsOrderDragKey | null {
    const raw = dt.getData(WMS_ORDER_DND_MIME);
    if (!raw) return null;
    try {
        const v = JSON.parse(raw) as Record<string, unknown>;
        const pick = (k: string) => (typeof v?.[k] === 'string' ? (v[k] as string) : null);
        return wmsOrderKeyOf({
            wmsSource: pick('wmsSource'), businessUnit: pick('businessUnit'),
            orderType: pick('orderType'), orderNumber: pick('orderNumber'),
        });
    } catch {
        return null;
    }
}
